import * as React from "react";
import { differenceInDays } from "date-fns";

import {
  Paper,
  Grid,
  Typography,
  Close,
  IconButton,
  TextField,
  Divider,
  Button,
  DesktopDatePicker,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  Alert,
} from "mpulse-design-system";
import { styled, Box, Menu } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";

import MetadataService from "../../services/Metadata/MetadataService";

import FieldChoiceOptionsMenu from "../FieldChoiceOptionsMenu/FieldChoiceOptionsMenu";
import MetadataStatusChip from "../MetadataStatusChip/MetadataStatusChip";
import AudioStatusChip from "../AudioStatusChip/AudioStatusChip";
import PopupLayout from "../PopupLayout/PopupLayout";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import DropDownField from "../DropDownField/DropDownField";
import StateOptions from "../../utils/options/state.js";
import constants from "../../constants";

const MetadataPopup = ({
  index,
  account,
  isNew,
  isViewMode,
  isEditMode,
  handleEdit,
  setPopupProps,
  prependToData,
  metadata = {},
}) => {
  const priorities = ["High", "Medium", "Low"];
  const requestedPublishDate =
    metadata.requested_publish_date || new Date().toLocaleDateString();
  const mPriority = metadata.priority || priorities[1];

  const [rerender, setRerender] = React.useState(true);
  const [publishDate, setPublishDate] = React.useState(requestedPublishDate);
  const [priority, setPriority] = React.useState(mPriority);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [metadataState, setMetadata] = React.useState(metadata);
  const [formErrors, setFormErrors] = React.useState({});
  const [addAnotherMetadata, setAddAnotherMetadata] = React.useState(false);
  const [clientDepartmentIdExistsError, setIdClientDepartmentIdExistsError] =
    React.useState(false);
  const open = Boolean(anchorEl);

  const accountId = account.id;
  const { metadataLabel, accountFields, supportedLanguages } = account;
  const { fields, fieldsOrder, booleanFields, recordedFields } = accountFields;
  const today = new Date();

  // virtual fields & filters
  const [isVirtual, setIsVirtual] = React.useState(metadata.is_virtual);
  // TODO: needs to be changed to support amore than providence
  const virtualExcludedFields = ["city", "state", "address"];

  // virtual filtered fields
  const filteredRecordedFields = recordedFields.filter(
    (f) => !virtualExcludedFields.includes(fields[f].name)
  );
  const filteredFieldsOrder = fieldsOrder.filter(
    (f) => !virtualExcludedFields.includes(fields[f].name)
  );
  const filteredFields = Object.fromEntries(
    Object.entries(fields).filter(
      ([k, v]) => !virtualExcludedFields.includes(v.name)
    )
  );

  // virtual/in person switch variables
  const [activeRecordedFields, setActiveRecordedFields] =
    React.useState(recordedFields);
  const [activeFields, setActiveFields] = React.useState(fieldsOrder);
  const [requiredFields, setRequiredFields] = React.useState(fields);

  const handleClose = () => {
    setPopupProps({
      hidden: true,
    });
  };

  const handleDateChange = (value) => {
    setPublishDate(value.toLocaleDateString());
    if (formErrors["requested_publish_date"])
      setFormErrors({ ...formErrors, ["requested_publish_date"]: false });
  };

  const handlePriorityClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handlePriorityClose = () => {
    setAnchorEl(null);
  };

  const handleSelectPriority = (e) => {
    setPriority(e.target.id || e.target.firstChild.id);
    handlePriorityClose();
  };

  const handleMetadataChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setMetadata((state) => ({ ...state, [name]: value }));
    setIdClientDepartmentIdExistsError(false);
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: false });
  };

  const handleStateChange = (e, v) => {
    const name = "state";
    const value = !(v == null) ? v : "";
    setMetadata((state) => ({ ...state, [name]: value }));
  };

  const handleBooleanMetadataChange = (e) => {
    const name = e.target.name;
    const value = e.target.checked;
    setMetadata((state) => ({ ...state, [name]: value }));
  };

  const handleAddAnother = (e) => {
    setAddAnotherMetadata(e.currentTarget.checked);
  };

  const saveAsDraft = (e) => {
    submit(e, true);
  };

  const returnHelperText = (field) => {
    return (isNew || isEditMode) && field.required_for_production
      ? "Required"
      : "";
  };

  const handleInPersonButton = () => {
    setIsVirtual(false);
  };

  const handleVirtualButton = () => {
    setIsVirtual(true);
  };

  React.useEffect(() => {
    if (isVirtual) {
      setActiveFields(filteredFieldsOrder);
      setActiveRecordedFields(filteredRecordedFields);
      setRequiredFields(filteredFields);
    } else {
      setActiveFields(fieldsOrder);
      setActiveRecordedFields(recordedFields);
      setRequiredFields(fields);
    }
  }, [isVirtual]);

  const fieldItem = (field, i) => {
    return field.name === "state" ? (
      <DropDownField
        dataTestId={`active-field-${field.name}`}
        options={StateOptions}
        defaultValue={
          isViewMode || isEditMode ? metadataState.data[field.name] : ""
        }
        handleChange={handleStateChange}
        label={field.label}
        disabled={isViewMode}
        helperText={returnHelperText(field)}
        error={formErrors[field.name]}
        reset={rerender}
      />
    ) : (
      <TextField
        data-testid={`active-field-${field.name}`}
        error={formErrors[field.name]}
        key={i}
        name={field.name}
        label={`${field.label}:`}
        defaultValue={
          isViewMode || isEditMode ? metadataState.data[field.name] : null
        }
        placeholder={isViewMode ? "" : "Input text"}
        helperText={
          (isNew || isEditMode) && field.required_for_production
            ? "Required"
            : ""
        }
        onChange={handleMetadataChange}
        value={metadataState[field.name]}
        InputProps={{
          readOnly: isViewMode,
        }}
        sx={{
          pointerEvents: isViewMode ? "none" : "all",
        }}
      />
    );
  };

  const submit = (e, saveAsDraft = false) => {
    const testValueLength = (value, isState) => {
      if (!value) return true;
      return isState ? value.length < 2 || value.length > 2 : value.length < 3;
    };
    e.preventDefault();
    let errors = {},
      data = {},
      pronunciations = {};

    // Validate all required fields have data
    for (const field in requiredFields) {
      const value = metadataState[fields[field].name];

      if (
        fields[field].required_for_production &&
        fields[field].type !== "BOOLEAN"
      ) {
        const condition = saveAsDraft
          ? false
          : value === ""
          ? true
          : testValueLength(
              isEditMode ? value || metadata.data[fields[field].name] : value,
              fields[field].name === "state"
            );
        if (condition) {
          errors[fields[field].name] = true;
        }
        if (fields[field].required_for_ivr) {
          let content = {};
          for (const language in supportedLanguages) {
            const pronunciation =
              metadataState[
                `${fields[field].name};${supportedLanguages[language]};pronunciation`
              ];
            if (pronunciation)
              content[supportedLanguages[language].toUpperCase()] =
                pronunciation;
          }
          if (Object.keys(content).length > 0)
            pronunciations[fields[field].name] = content;
        }
      }
      if (
        fields[field].type === "BOOLEAN" &&
        (isNew || (isEditMode && typeof value != "undefined"))
      ) {
        data[fields[field].name] =
          typeof value === "undefined"
            ? false
            : typeof value === "boolean"
            ? value
            : false;
      } else if (value) {
        if (
          fields[field].validation_pattern &&
          !value.match(fields[field].validation_pattern)
        ) {
          // If the field has a validation pattern and the value does not match it, return an error.
          errors[fields[field].name] = true;
        } else {
          data[fields[field].name] = value;
        }
      }
    }

    const current_department_id =
      metadata["client_department_id"] || metadataState["client_department_id"];
    const new_department_id = metadataState["client_department_id"];

    const testValidInt = (id) => {
      return !id || isNaN(id) || isNaN(parseInt(id));
    };
    if (
      testValidInt(current_department_id) ||
      testValidInt(new_department_id)
    ) {
      errors["client_department_id"] = true;
    }

    const selectedPublishDate = new Date(publishDate);
    const diff = differenceInDays(today, selectedPublishDate);
    if (diff >= 1) {
      errors["requested_publish_date"] = true;
    }

    if (Object.keys(errors).length !== 0) {
      setFormErrors(errors);
      return false;
    } else {
      setFormErrors(errors);
      if (
        isNew || // new object
        (isEditMode && metadata.status === "DRAFT") ||
        (isEditMode &&
          (Object.keys(data).length > 0 ||
            Object.keys(pronunciations).length > 0)) || // main data changed
        priority !== metadata.priority || // priority changed
        requestedPublishDate !== publishDate || // publish date changed
        current_department_id !== new_department_id // Department ID changed
      ) {
        addOrUpdateMetadata(
          current_department_id,
          new_department_id,
          data,
          pronunciations,
          isVirtual,
          saveAsDraft,
          isNew
        );
      } else handleClose();
    }
  };

  const addOrUpdateMetadata = (
    client_department_id,
    new_department_id,
    data,
    pronunciations,
    isVirtual,
    saveAsDraft,
    isNew
  ) => {
    MetadataService.addOrUpdateMetadata(
      accountId,
      client_department_id,
      new_department_id,
      data,
      pronunciations,
      isVirtual,
      saveAsDraft,
      {
        priority,
        publishDate,
      },
      isNew
    )
      .then((data) => {
        if (isEditMode) {
          prependToData(data, index);
        } else {
          prependToData(data);
        }
        if (addAnotherMetadata && isNew) {
          document.getElementById("metadata-popup-form").reset();
          setRerender(!rerender);
          setPriority(priorities[1]);
          setPublishDate(requestedPublishDate);
          // clear metadata state values that the text fields are using
          Object.keys(metadataState).forEach((v) => (metadataState[v] = ""));
          setMetadata({ ...metadataState });
        } else {
          handleClose();
        }
      })
      .catch((err) => {
        // TODO: ADD Error handling
        if ("non_field_errors" in err.response.data) {
          if (
            err.response.data["non_field_errors"].find(
              (error) =>
                error ===
                "This Client_Department already exists in this account!"
            )
          ) {
            setIdClientDepartmentIdExistsError(true);
            setFormErrors({ ...formErrors, ["client_department_id"]: true });
          }
          if (
            err.response.data["non_field_errors"].find(
              (error) => error === "Requested publish date is not valid"
            )
          ) {
            setFormErrors({ ...formErrors, ["requested_publish_date"]: true });
          }
        }
      });
  };

  const DepartmentType = () => (
    <Box
      sx={{
        paddingTop: "10px",
        display: "flex",
        flexDirection: "column",
        height: "80px",
      }}
    >
      <Typography color="textSecondary" variant="overline">
        DEPARTMENT TYPE
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "40px",
          paddingTop: "5px",
          paddingBottom: "5px",
        }}
      >
        <Button
          data-testid="in-person-button"
          disabled={isViewMode || isEditMode}
          sx={{
            flex: 1,
            borderTopRightRadius: "0px",
            borderBottomRightRadius: "0px",
          }}
          variant={isVirtual ? "outlined" : "contained"}
          onClick={handleInPersonButton}
        >
          IN-PERSON DEPARTMENT
        </Button>
        <Button
          data-testid="virtual-button"
          disabled={isViewMode || isEditMode}
          sx={{
            flex: 1,
            borderTopLeftRadius: "0px",
            borderBottomLeftRadius: "0px",
          }}
          variant={isVirtual ? "contained" : "outlined"}
          onClick={handleVirtualButton}
        >
          VIRTUAL-DEPARTMENT
        </Button>
      </Box>
      <Typography color={(theme) => theme.palette.grey[550]} variant="body2">
        Required
      </Typography>
    </Box>
  );
  return (
    <PopupLayout>
      <Paper
        elevation={3}
        sx={{
          width: "556px",
          height: "fit-content",
          padding: (theme) => `${theme.spacing(3)} 0`,
          position: "relative",

          "& .popup-padding": {
            padding: (theme) => `0 ${theme.spacing(3)}`,
          },
        }}
      >
        <form id="metadata-popup-form" onSubmit={submit}>
          <StyledIconButton color="tertiary" onClick={handleClose}>
            <Close color="tertiary" />
          </StyledIconButton>
          <Grid
            container
            rowSpacing={3}
            sx={{ paddingBottom: (theme) => `${theme.spacing(3)} !important` }}
            className="popup-padding"
          >
            <Grid item container xxs={12} rowSpacing={1}>
              <Grid item xxs={12}>
                <Typography color="textPrimary" variant="body1">
                  {isNew
                    ? `Add ${metadataLabel}`
                    : `${metadataLabel.toUpperCase()} DETAILS`}
                </Typography>
              </Grid>
              {!isNew ? (
                <Grid item container xxs={12} columnSpacing={2}>
                  <Grid item xs="auto" sx={{ maxWidth: "450px !important" }}>
                    <Typography color="textPrimary" variant="h2">
                      {metadataState.data.department_name ||
                        metadataState.data.friendly_name}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <MetadataStatusChip status={metadataState.status} />
                  </Grid>
                </Grid>
              ) : null}
            </Grid>
            <Grid item xxs={12}>
              <Typography color="textPrimary" variant="h3">
                {metadataLabel} Information
              </Typography>
            </Grid>
          </Grid>
          <Box
            sx={{
              height: "55vh !important",
              overflow: "scroll",
            }}
            className="popup-padding"
          >
            <Grid container rowSpacing={3}>
              <Grid item container rowSpacing={2}>
                <Grid item xxs={12}>
                  <TextField
                    error={formErrors["client_department_id"]}
                    defaultValue={
                      isViewMode || isEditMode
                        ? metadataState.client_department_id
                        : null
                    }
                    name={"client_department_id"}
                    label={`${metadataLabel} ID:`}
                    placeholder="Input text"
                    helperText={
                      isViewMode
                        ? ""
                        : clientDepartmentIdExistsError
                        ? "This Department ID is already in use. Please enter a different ID."
                        : "Required"
                    }
                    onChange={handleMetadataChange}
                    InputProps={{
                      readOnly: isViewMode,
                    }}
                    sx={{
                      pointerEvents: isViewMode ? "none" : "all",
                    }}
                  />
                  <DepartmentType></DepartmentType>
                </Grid>
                {activeFields.map((key, i) => {
                  const field = fields[key];
                  if (field.type.startsWith("FIELD_CHOICE_")) {
                    return (
                      <FieldChoiceOptionsMenu
                        data-testid={`active-field-${field.name}`}
                        key={key}
                        {...{
                          i,
                          field,
                          error: formErrors[field.name],
                          fieldId: key,
                          accountId,
                          handleMetadataChange,
                          isNew,
                          isViewMode,
                          isEditMode,
                          reset: rerender,
                          addAnotherMetadata,
                          value: metadataState.data
                            ? metadataState.data[field.name]
                            : undefined,
                        }}
                      />
                    );
                  }
                  return (
                    <Grid key={`field-${i}`} item xxs={12}>
                      {fieldItem(field, i)}
                    </Grid>
                  );
                })}
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {activeRecordedFields.map((key, i) => {
                const field = fields[key];
                return (
                  <Grid key={i} item container rowSpacing={3}>
                    <Grid
                      key={`container-${i}`}
                      item
                      container
                      xs={12}
                      rowSpacing={2}
                    >
                      <Grid key={`field-${i}`} item xxs={12}>
                        <TextField
                          data-testid={`active-field-${field.name}`}
                          defaultValue={
                            isViewMode || isEditMode
                              ? metadataState.data[field.name]
                              : null
                          }
                          error={formErrors[field.name]}
                          name={field.name}
                          key={`text-field-${i}`}
                          label={`${field.label}:`}
                          placeholder={isViewMode ? "" : "Input text"}
                          helperText={isNew || isEditMode ? "Required" : ""}
                          onChange={handleMetadataChange}
                          value={metadataState[field.name]}
                          InputProps={{
                            readOnly: isViewMode,
                          }}
                          sx={{
                            pointerEvents: isViewMode ? "none" : "all",
                          }}
                        />
                      </Grid>
                      {supportedLanguages.map((language) => {
                        return (
                          <Grid
                            key={`pronunciation-${i}-${language}`}
                            item
                            xxs={12}
                          >
                            <TextField
                              defaultValue={
                                (isViewMode || isEditMode) &&
                                metadataState.pronunciations[field.name]
                                  ? metadataState.pronunciations[field.name][
                                      language.toUpperCase()
                                    ]
                                  : null
                              }
                              error={
                                formErrors[
                                  `${field.name};${language};pronunciation`
                                ]
                              }
                              name={`${field.name};${language};pronunciation`}
                              key={`text-field-${i}-${language}`}
                              placeholder={isViewMode ? "" : "Input text"}
                              label={`Pronunciation Notes (${language.toUpperCase()})`}
                              onChange={handleMetadataChange}
                              InputProps={{
                                readOnly: isViewMode,
                              }}
                              sx={{
                                pointerEvents: isViewMode ? "none" : "all",
                              }}
                            />
                          </Grid>
                        );
                      })}
                      {(isViewMode || isEditMode) &&
                      metadataState.audio[field.name]
                        ? metadataState.audio[field.name].map((audio) => {
                            if (
                              audio.status &&
                              audio.status !== "READY_FOR_RECORDING"
                            ) {
                              return (
                                <Grid
                                  item
                                  container
                                  xxs={12}
                                  key={`audio-container-${audio.id}`}
                                >
                                  <Grid item xxs={12}>
                                    <Typography
                                      color="textSecondary"
                                      variant="overline"
                                    >
                                      AUDIO FILE ({audio.language})
                                    </Typography>
                                  </Grid>
                                  <Grid item xs>
                                    <AudioPlayer
                                      url={audio.filepath}
                                      duration={audio.duration || "00:00:00"}
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    direction="row"
                                    justfyContent="flex-end"
                                  >
                                    <AudioStatusChip status={audio.status} />
                                  </Grid>
                                </Grid>
                              );
                            }
                          })
                        : null}
                    </Grid>
                    <Grid key={`divider-container-${i}`} item xxs={12}>
                      <Divider key={`divider-${i}`} />
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
            <Grid
              container
              sx={{ paddingTop: (theme) => theme.spacing(3) }}
              rowSpacing={3}
            >
              <Grid item xxs={12}>
                <Typography color="textPrimary" variant="h3">
                  Settings
                </Typography>
              </Grid>
              {booleanFields && booleanFields.length >= 1 ? (
                <>
                  <Grid item container xxs={12}>
                    {booleanFields.map((key, i) => {
                      const field = fields[key];
                      return (
                        <Grid key={`container-${i}`} item container>
                          <Grid item key={`field-${i}`} sx={{ flexGrow: 1 }}>
                            <Typography
                              key={`field-name-${i}`}
                              color="textSecondary"
                              variant="overline"
                            >
                              {field.label}
                            </Typography>
                          </Grid>
                          <Grid item key={`switch-${i}`}>
                            <Switch
                              name={field.name}
                              checked={
                                typeof metadataState[field.name] === "boolean"
                                  ? metadataState[field.name]
                                  : metadataState.data &&
                                    typeof metadataState.data[field.name] ===
                                      "boolean"
                                  ? metadataState.data[field.name]
                                  : false
                              }
                              onChange={handleBooleanMetadataChange}
                              sx={{
                                pointerEvents: isViewMode ? "none" : "all",
                              }}
                            />
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Grid>
                  <Grid item xxs={12}>
                    <Divider />
                  </Grid>
                </>
              ) : null}
              <Grid item container xxs={12}>
                <Grid item sx={{ flexGrow: 1 }}>
                  <Typography color="textSecondary" variant="overline">
                    Priority
                  </Typography>
                </Grid>
                <Grid item>
                  <StyledButton
                    id="priority"
                    aria-controls={open ? "audio-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    variant="outlined"
                    disableElevation
                    color="inherit"
                    size="large"
                    onClick={handlePriorityClick}
                    endIcon={isViewMode ? null : <ArrowDropDown />}
                    sx={{
                      pointerEvents: isViewMode ? "none" : "all",
                    }}
                  >
                    {priority}
                  </StyledButton>
                  <Menu
                    id="audio-menu"
                    MenuListProps={{
                      "aria-labelledby": "audio-actions",
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handlePriorityClose}
                    PaperProps={{
                      sx: {
                        width: anchorEl && anchorEl.offsetWidth,
                        ".MuiList-root": {
                          padding: 0,
                        },
                        "& .MuiMenuItem-root": {
                          height: "40px",
                          "&:hover": {
                            "& .MuiTypography-root": {
                              color: (theme) =>
                                `${theme.palette.primary.light} !important`,
                            },
                          },
                        },
                      },
                    }}
                  >
                    {priorities.map((p, i) => {
                      return (
                        <MenuItem
                          key={`menu-item-${i}`}
                          disableRipple
                          onClick={handleSelectPriority}
                        >
                          <Typography
                            key={`menu-label-${i}`}
                            id={p}
                            color="textPrimary"
                            variant="body1"
                          >
                            {p}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Grid>
              </Grid>
              <Grid item xxs={12}>
                <Divider />
              </Grid>
              <Grid item container xxs={12}>
                <Grid item sx={{ flexGrow: 1 }}>
                  <Typography color="textSecondary" variant="overline">
                    Requested Publish Date
                  </Typography>
                </Grid>
                <Grid item>
                  <StyledDatePicker
                    sx={{
                      pointerEvents: isViewMode ? "none" : "all",
                    }}
                  >
                    <DesktopDatePicker
                      data-testid="date-picker"
                      onChange={handleDateChange}
                      value={publishDate}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={formErrors["requested_publish_date"]}
                          helperText={
                            formErrors["requested_publish_date"]
                              ? constants.metadata.errors.publishDateError
                              : null
                          }
                        />
                      )}
                      minDate={today}
                    />
                  </StyledDatePicker>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Grid
            container
            sx={{ paddingTop: (theme) => `${theme.spacing(7)} !important` }}
            className="popup-padding"
            spacing={2}
          >
            {Object.keys(formErrors).length !== 0 ? (
              <Grid item xs={12}>
                <Alert severity="error">Please correct the errors above.</Alert>
              </Grid>
            ) : null}
            <Grid item sx={{ flexGrow: isNew ? 0 : 1 }}>
              <Button
                id="metadata-cancel"
                variant="outlined"
                size="medium"
                onClick={handleClose}
              >
                CANCEL
              </Button>
            </Grid>
            {isNew || metadataState.status == "DRAFT" ? (
              <>
                <Grid item sx={{ flexGrow: 1, marginTop: "10px" }}>
                  <a onClick={saveAsDraft}>
                    <Typography color="textPrimary">Save Draft</Typography>
                  </a>
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="addAnother"
                        size="medium"
                        checked={addAnotherMetadata}
                        onChange={handleAddAnother}
                      />
                    }
                    label={`Add another ${metadataLabel.toLowerCase()}`}
                    sx={{
                      marginRight: "unset",
                      marginTop: "10px",
                      "& .MuiCheckbox-root": {
                        padding: "unset !important",
                      },
                    }}
                  />
                </Grid>
              </>
            ) : null}
            <Grid item>
              {isViewMode ? null : (
                // <Button
                //   id="metadata-submit"
                //   variant="contained"
                //   size="medium"
                //   type="button"
                //   onClick={handleEdit}
                // >
                //   Edit
                // </Button>
                <Button
                  data-testid="metadata-submit"
                  id="metadata-submit"
                  variant="contained"
                  size="medium"
                  type="submit"
                >
                  Submit
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </PopupLayout>
  );
};

const StyledIconButton = styled(IconButton)(() => ({
    top: "2px",
    right: "4px",
    position: "absolute",
  })),
  StyledDatePicker = styled(Box)(() => ({
    width: "150px",
    "& .MuiInputBase-root": {
      height: "40px",
    },
  })),
  StyledButton = styled(Button)(({ theme }) => ({
    justifyContent: "space-between",
    width: "150px",
    height: "40px",
    borderColor: theme.palette.grey[300],
    color: theme.palette.grey[550],
    letterSpacing: 0,
    textTransform: "unset !important",
    ...theme.typography.body1,
  }));

export default MetadataPopup;
